import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TimelineComponent from "./Timeline"
import { useDispatch, useSelector } from "react-redux";
import { CompactSelect, FilterSelect, SearchComponent, SelectOption } from '../../components/common/Search';
import SprintSortButton from '../../components/common/Search/SprintSortButton';
import HierarchicalFilterDropdown, { HierarchicalFilterDropdownRef } from '../../components/common/Search/HierarchicalFilterDropdown';
import { useResourcesService } from '../../services';
import useIntegrationStatus from '../../hooks/useIntegrationStatus';
import moment from 'moment';
import { extractCapacityPlanningTasks } from '../../utils/helperUtils';
import UnallocatedTasksWarningModal from '../../components/Modals/UnallocatedTasksWarningModal';
import {
  loadCapacityPlanningFiltersForOrg,
  resetTeamFilterState,
  type SearchType,
  setCapacityPlanningPayload,
  setTeamSprints,
  setUtilizationCurrentDateRange,
  setUtilizationDebouncedSearchText,
  setUtilizationIsInitialized,
  updateCapacityPlanningFilters,
  updateTeamFilterState
} from '../../redux/slices/resources';
import { useJwt } from '../../helpers/common';
import { useUnallocatedTasksWarning } from '../../hooks/useUnallocatedTasksWarning';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import { ComponentPage } from '../../constants/constants';


// Helper function to get default date range (±2 weeks from today, 4 weeks total)
const getDefaultDateRange = () => {
  const today = moment();
  return {
    startDate: today.clone().subtract(2, 'weeks').format('YYYY-MM-DD'),
    endDate: today.clone().add(2, 'weeks').format('YYYY-MM-DD')
  };
};

// Helper function to truncate text with ellipsis
const truncateText = (text: string, maxLength: number = 50): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Helper to normalize sprint status for grouping
const normalizeSprintStatus = (status: string): string => {
  if (status === 'past' || status === 'completed') return 'past';
  if (status === 'current') return 'current';
  return 'future';
};

// Helper to build a composite sprint key: "name::normalizedStatus"
const buildSprintKey = (name: string, status: string): string => {
  return `${name}::${normalizeSprintStatus(status)}`;
};

// Helper to extract sprint name from composite key
const getSprintNameFromKey = (key: string): string => {
  const idx = key.lastIndexOf('::');
  return idx >= 0 ? key.substring(0, idx) : key;
};

// Sprint status priority for sorting (lower = shown first)
const getStatusPriority = (status: string): number => {
  const normalized = normalizeSprintStatus(status);
  if (normalized === 'past') return 1;
  if (normalized === 'current') return 2;
  return 3; // future
};

// Convert status to display text
const getStatusDisplayText = (status: string): string => {
  const normalized = normalizeSprintStatus(status);
  if (normalized === 'current') return 'Current';
  if (normalized === 'past') return 'Past';
  return 'Future';
};

// Build set of active team IDs that have members (used for sprint team validation)
const getActiveTeamIds = (organizationTeams: any[] | null, resources: any[] | null): Set<string> => {
  const teamIds = new Set<string>();
  if (!organizationTeams) return teamIds;

  organizationTeams.forEach((team: any) => {
    if (team?.is_deactivated !== true && Array.isArray(team?.members) && team.members.length > 0) {
      teamIds.add(String(team.id));
    }
  });

  // Also include teams from active resources
  (resources || []).filter((res: any) => res?.is_deleted === false).forEach((res: any) => {
    if (Array.isArray(res?.teams)) {
      res.teams.forEach((t: any) => t?.id != null && teamIds.add(String(t.id)));
    }
  });

  return teamIds;
};

/**
 * Determines the visibility/state of a sprint option based on its teams.
 * Returns: 
 *   - { action: 'enable' } – at least one valid team, sprint is selectable
 *   - { action: 'hide' } – all teams are deactivated, hide from dropdown entirely
 *   - { action: 'disable', reason } – teams not imported or have no members, show as disabled
 */
const getSprintTeamStatus = (
  sprints: any[],
  teamByTeamId: Map<string, any> | null,
  activeTeamIds: Set<string>
): { action: 'enable' | 'disable' | 'hide'; reason?: string } => {
  if (!teamByTeamId) return { action: 'enable' }; // Don't restrict if teams haven't loaded

  const notImported: string[] = [];
  const noMembers: string[] = [];
  let allDeactivated = true;
  let hasValidTeam = false;

  for (const sprint of sprints) {
    const orgTeam = teamByTeamId.get(String(sprint.team_id));

    if (!orgTeam) {
      allDeactivated = false;
      const teamLabel = sprint.team_name
        ? `${sprint.team_name}${sprint.project_name ? ` (${sprint.project_name})` : ''}`
        : 'Unknown team';
      notImported.push(teamLabel);
    } else if (orgTeam.is_deactivated === true) {
      // Deactivated – continue checking others
    } else if (!activeTeamIds.has(String(orgTeam.id))) {
      allDeactivated = false;
      const orgLabel = `${orgTeam.name}${sprint.project_name ? ` (${sprint.project_name})` : ''}`;
      noMembers.push(orgLabel);
    } else {
      hasValidTeam = true;
      break;
    }
  }

  if (hasValidTeam) return { action: 'enable' };

  // If every team is deactivated (none are not-imported or no-members), hide entirely
  if (allDeactivated && notImported.length === 0 && noMembers.length === 0) {
    return { action: 'hide' };
  }

  // Build a user-friendly message for remaining issues
  const parts: string[] = [];
  if (notImported.length > 0) {
    const teams = Array.from(new Set(notImported)).join(', ');
    parts.push(`${teams} – not yet imported`);
  }
  if (noMembers.length > 0) {
    const teams = Array.from(new Set(noMembers)).join(', ');
    parts.push(`${teams} – no members assigned`);
  }

  return {
    action: 'disable',
    reason: `Unable to select this sprint:\n${parts.join('\n')}`
  };
};

interface ResourcesUtilizationsProps {
  resources: any;
}

const ResourcesUtilizations = ({ resources }: ResourcesUtilizationsProps) => {

  const dispatch = useDispatch();
  const JWTdata: any = useJwt();
  const org_id = JWTdata?.org_id;
  const { isIntegrationActive } = useIntegrationStatus();
  const azureActive = isIntegrationActive('azure');
  const jiraActive = isIntegrationActive('jira');
  const hasActiveIntegrations = azureActive || jiraActive;
  const [groups, setGroups] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState<Record<string, any>>({});
  const [filteredResources, setFilteredResources] = useState([]);
  const [displayResources, setDisplayResources] = useState<any[]>([]); // Separate state for resources to display in timeline
  const [availableSkills, setAvailableSkills] = useState<SelectOption[]>([]);
  const [availableTeams, setAvailableTeams] = useState<SelectOption[]>([]);
  const [availableSprints, setAvailableSprints] = useState<SelectOption[]>([]);
  const [sortedSprintsForFilter, setSortedSprintsForFilter] = useState<any[]>([]);
  const [currentSprintIndexForFilter, setCurrentSprintIndexForFilter] = useState<number>(-1);
  const [teamSprintsForNavigation, setTeamSprintsForNavigation] = useState<any[]>([]); // Team's sprints for navigation

  const [selectedResourceOptions, setSelectedResourceOptions] = useState<SelectOption[]>([]);
  const [selectedSkillOptions, setSelectedSkillOptions] = useState<SelectOption[]>([]);
  const [selectedSprintTeamOptions, setSelectedSprintTeamOptions] = useState<SelectOption[]>([]); // For multiselect teams in sprint mode
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');
  const hierarchicalFilterRef = useRef<HierarchicalFilterDropdownRef | null>(null);
  // Warning modal state
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  // Ref to track previous payload to prevent duplicate API calls
  const previousPayloadRef = useRef<string | null>(null);
  // Ref to track selected teams for use in effects without triggering re-renders
  // Ref to track selected teams for use in effects without triggering re-renders
  const selectedTeamsRef = useRef<string[]>([]);
  // Ref to track the last sprint for which we auto-selected a team
  const lastAutoSelectedSprint = useRef<string | null>(null);



  const {
    capacityPlanningData,
    capacityPlanningLoading,
    capacityPlanningError,
    suggestedTasks,
    utilizationFilters: {
      currentDateRange,
      isInitialized
    }, 
    resourcePageSprints,
    teamFilterState,
    teamSprints,
    capacityPlanningFilters,
    capacityPlanningPayload
  } = useSelector((state: any) => state.resources);
  const { teams: organizationTeams, teamsLoading, skills: organizationSkills } = useSelector((state: any) => state.organization);
  const { manageResourcesLoading } = useSelector((state: any) => state.resources);

  // --- URL Filters Hook ---
  // We use the URL as the source of truth, but we sync it DOWN into Redux
  // so the deeply nested Timelines don't have to change their Redux selectors
  const { filters: urlFilters, setFilter, setFilters } = useUrlFilters([
    { urlKey: 'type', storageKey: 'res_searchType', defaultValue: 'people' },
    { urlKey: 'resources', storageKey: 'res_selectedResources', defaultValue: '', multi: true },
    { urlKey: 'skills', storageKey: 'res_selectedSkills', defaultValue: '', multi: true },
    { urlKey: 'team', storageKey: 'res_selectedTeam', defaultValue: '' },
    { urlKey: 'teams', storageKey: 'res_selectedTeams', defaultValue: '', multi: true },
    { urlKey: 'sprint', storageKey: 'res_selectedSprint', defaultValue: '' },
    { urlKey: 'sprintSortBy', storageKey: 'res_sprintSortBy', defaultValue: 'latest' },
    { urlKey: 'sprintRange', storageKey: 'res_sprintRange', defaultValue: 'current' },
    { urlKey: 'sprintStatus', storageKey: 'res_sprintStatus', defaultValue: 'all' },
    { urlKey: 'sprintSkills', storageKey: 'res_sprintSkills', defaultValue: '', multi: true },
    { urlKey: 'sprintWorkItems', storageKey: 'res_sprintWorkItems', defaultValue: '', multi: true },
    { urlKey: 'status', storageKey: 'res_status', defaultValue: '', multi: true },
    { urlKey: 'startDate', storageKey: 'res_startDate', defaultValue: '' },
    { urlKey: 'endDate', storageKey: 'res_endDate', defaultValue: '' }
  ], ComponentPage.PEOPLE);
  
  const sprintStatusFilter = (urlFilters.sprintStatus as string) || 'all';

  const {
    searchType,
    selectedResources,
    selectedSkills,
    selectedTeam,
    selectedTeams,
    selectedSprint,
    sprintSortBy,
    sprintRangeSelection,
    searchText: localSearchText,
    sprintFilters
  } = capacityPlanningFilters;

  // --- SYNCHRONIZE URL TO REDUX ---
  // We only push changes to Redux when the URL filters themselves actually change.
  // We do NOT include Redux values in the dep array — that would cause a circular loop
  // because other effects legitimately modify Redux state (e.g. auto-select teams).
  const prevUrlFiltersRef = useRef<string>('');

  useEffect(() => {
    if (!org_id) return;

    // Serialize current URL filters to detect actual changes
    const currentSnapshot = JSON.stringify(urlFilters);
    if (currentSnapshot === prevUrlFiltersRef.current) return;
    prevUrlFiltersRef.current = currentSnapshot;

    // Push all URL filter values to Redux unconditionally
    // This is safe because this effect only fires when the URL actually changes
    const updates: any = {};
    if (urlFilters.type) updates.searchType = urlFilters.type;
    if (urlFilters.team !== undefined) updates.selectedTeam = urlFilters.team;
    if (urlFilters.sprint !== undefined) updates.selectedSprint = urlFilters.sprint;
    if (urlFilters.sprintSortBy !== undefined) updates.sprintSortBy = urlFilters.sprintSortBy;
    if (urlFilters.sprintRange !== undefined) updates.sprintRangeSelection = urlFilters.sprintRange;
    if (Array.isArray(urlFilters.resources)) updates.selectedResources = urlFilters.resources;
    if (Array.isArray(urlFilters.skills)) updates.selectedSkills = urlFilters.skills;
    if (Array.isArray(urlFilters.teams)) updates.selectedTeams = urlFilters.teams;

    if (urlFilters.sprintSkills !== undefined || urlFilters.sprintWorkItems !== undefined) {
      updates.sprintFilters = {
        skills: Array.isArray(urlFilters.sprintSkills) ? urlFilters.sprintSkills : [],
        work_item_type: Array.isArray(urlFilters.sprintWorkItems) ? urlFilters.sprintWorkItems : []
      };
    }

    if (Object.keys(updates).length > 0) {
      dispatch(updateCapacityPlanningFilters({ filters: updates, orgId: org_id }));
    }

    if (urlFilters.startDate && urlFilters.endDate) {
      dispatch(setUtilizationCurrentDateRange({
        startDate: urlFilters.startDate as string,
        endDate: urlFilters.endDate as string
      }));
    }
  }, [urlFilters, org_id, dispatch]);

  const availableTeamsForSprint = useMemo(() => {
    if (searchType !== 'sprint' || !selectedSprint || !organizationTeams || !availableSprints.length) {
      return [];
    }

    const sprintOption = availableSprints.find((s: any) => s.value === selectedSprint) as any;
    if (!sprintOption || !sprintOption.metadata || !sprintOption.metadata.sprints) {
      return [];
    }

    const sprintTeamIds = new Set(
      sprintOption.metadata.sprints.map((sprint: any) => String(sprint.team_id))
    );

    const teamIdsWithMembers = new Set<string>();
    (organizationTeams as any[]).forEach((team: any) => {
      if (Array.isArray(team?.members) && team.members.length > 0) {
        teamIdsWithMembers.add(String(team.id));
      }
    });

    (resources || []).filter((res: any) => res?.is_deleted === false).forEach((res: any) => {
      if (Array.isArray(res?.teams)) {
        res.teams.forEach((t: any) => t?.id != null && teamIdsWithMembers.add(String(t.id)));
      }
    });

    return (organizationTeams as any[])
      .filter((team: any) => {
        if (!team || !team.name || !String(team.name).trim()) return false;
        if (team?.is_deactivated === true) return false;
        if (!teamIdsWithMembers.has(String(team.id))) return false;
        return sprintTeamIds.has(String(team.team_id));
      })
      .map((team: any) => {
        const projectNames: string[] = Array.isArray(team.projects)
          ? team.projects.map((p: any) => p?.project_name).filter(Boolean)
          : [];
        const projectsSuffix = projectNames.length ? ` (${projectNames.join(', ')})` : '';
        const fullLabel = `${team.name}${projectsSuffix}`;
        return {
          value: String(team.id),
          label: truncateText(fullLabel, 50)
        } as SelectOption;
      })
      .sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label));
  }, [searchType, selectedSprint, availableSprints, organizationTeams, resources]);

  const { fetchCapacityPlanning, fetchCapacityPlanningSprint } = useResourcesService();

  // Use shared hook for warning check
  const { shouldShowWarning, unallocatedTasksCount, totalStoryPoints } = useUnallocatedTasksWarning();

  // Helper function to execute pending action after warning confirmation
  const executePendingAction = useCallback(() => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }, [pendingAction]);

  // Helper function to handle action with warning check
  const handleActionWithWarning = useCallback((action: () => void) => {
    // Don't show warning if in sprint mode but no teams are imported for the sprint
    const isSprintWithNoTeams = searchType === 'sprint' && availableTeamsForSprint.length === 0;
    if (shouldShowWarning && !isSprintWithNoTeams) {
      setPendingAction(() => action);
      setShowWarningModal(true);
    } else {
      action();
    }
  }, [shouldShowWarning, searchType, availableTeamsForSprint.length]);

  const getResourceOptions = useCallback(() => {
    if (!groups || !groups.length) return [];

    return groups
      .map((resource: any) => ({
        value: resource.id,
        label: resource.title
      }))
      .sort((a, b) => {
        const labelA = (a.label || '').toLowerCase();
        const labelB = (b.label || '').toLowerCase();
        return labelA.localeCompare(labelB);
      });
  }, [groups]);

  // Initialize selected options from Redux state when data is ready
  useEffect(() => {
    if (!groups || groups.length === 0) return;

    const resourceOptions = getResourceOptions();

    // Sync selectedResourceOptions with Redux selectedResources
    if (selectedResources.length > 0) {
      const options = resourceOptions.filter(o => selectedResources.includes(String(o.value)));
      setSelectedResourceOptions(options);
    } else {
      setSelectedResourceOptions([]);
    }
  }, [groups, selectedResources, getResourceOptions]);

  // Initialize selected skill options from Redux state when skills are available
  useEffect(() => {
    if (selectedSkills.length > 0 && availableSkills.length > 0) {
      const options = availableSkills.filter(o => selectedSkills.includes(String(o.value)));
      setSelectedSkillOptions(options);
    } else {
      setSelectedSkillOptions([]);
    }
  }, [availableSkills, selectedSkills]);

  // Initialize selected sprint team options from Redux state when sprint teams are available
  useEffect(() => {
    if (selectedTeams && selectedTeams.length > 0 && availableTeamsForSprint.length > 0) {
      const options = availableTeamsForSprint.filter(o => selectedTeams.includes(String(o.value)));
      setSelectedSprintTeamOptions(options);
    } else {
      setSelectedSprintTeamOptions([]);
    }
  }, [availableTeamsForSprint, selectedTeams]);

  // Sync selectedTeamsRef with state
  useEffect(() => {
    selectedTeamsRef.current = selectedTeams || [];
  }, [selectedTeams]);

  // URL param syncing removed - filters are now persisted in localStorage via Redux

  // Load capacity planning filters for the current org when org_id is available
  useEffect(() => {
    if (org_id) {
      dispatch(loadCapacityPlanningFiltersForOrg(org_id));
    }
  }, [org_id, dispatch]);

  // Initialize filters - only set default if truly needed
  useEffect(() => {
    if (!isInitialized && groups.length > 0) {
      // Don't reset searchType if it's already set from localStorage
      // Only reset if teams are unavailable AND we're trying to use team filter
      // But wait for teams to load first before making this decision
      dispatch(setUtilizationIsInitialized(true));
    }
  }, [groups, isInitialized, dispatch]);






  // Fallback to 'people' filter if sprint filter is selected but Azure is not active
  useEffect(() => {
    // Only check after initialization is complete
    if (!isInitialized) return;

    // If current filter is sprint but Azure is not active, fallback to people
    if (searchType === 'sprint' && !azureActive) {
      dispatch(updateCapacityPlanningFilters({ filters: { searchType: 'people' }, orgId: org_id }));
    }
  }, [searchType, azureActive, isInitialized, dispatch]);

  // Set default sprint status filter to 'current' when sprints are loaded and there are current sprints
  // Falls back to 'all' if no current sprints exist
  const [hasSetDefaultSprintFilter, setHasSetDefaultSprintFilter] = useState(false);
  useEffect(() => {
    if (hasSetDefaultSprintFilter) return; // Only set once
    if (searchType !== 'sprint') return; // Only for sprint filter
    if (!availableSprints || availableSprints.length === 0) return; // Wait for sprints to load

    // Check if there are any current sprints
    const hasCurrentSprints = availableSprints.some((opt: any) =>
      opt.metadata?.status === 'current'
    );

    if (hasCurrentSprints) {
      setFilter('sprintStatus', 'current');
    } else {
      setFilter('sprintStatus', 'all');
    }
    setHasSetDefaultSprintFilter(true);
  }, [searchType, availableSprints, hasSetDefaultSprintFilter]);

  // Debounce search text with 150ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(localSearchText);
      dispatch(setUtilizationDebouncedSearchText(localSearchText));
    }, 150);

    return () => clearTimeout(timer);
  }, [localSearchText]);

  useEffect(() => {
    if (resources && resources.length) {
      const visibleResources = resources.filter((res: any) => res?.is_deleted === false);
      const _resources = visibleResources
        .filter((res: any) => res.name && res.name.trim()) // Filter out resources without names
        .map((res: any) => {
          return {
            id: res.id,
            title: res.name,
            rightTitle: res.skills_name?.join(","),
            skills: res.skills_name || [],
            teams: res.teams || []
          }
        })
      setGroups(_resources)
      // Initialize filteredResourceIds with all resources (will be filtered by the filter effect)
      const allResourceIds = _resources.map((r: any) => r.id);
      dispatch(updateCapacityPlanningFilters({ filters: { filteredResourceIds: allResourceIds }, orgId: org_id }));

      // Extract unique skills from all resources
      const allSkills = new Set<string>();
      visibleResources.forEach((res: any) => {
        if (res.skills_name && Array.isArray(res.skills_name)) {
          res.skills_name.forEach((skill: string) => {
            if (skill && skill.trim()) {
              allSkills.add(skill);
            }
          });
        }
      });

      const skillOptions = Array.from(allSkills)
        .filter(skill => skill && skill.trim())
        .sort((a, b) => a.localeCompare(b))
        .map(skill => ({
          value: skill,
          label: skill
        }));

      setAvailableSkills(skillOptions);
    }
  }, [resources, dispatch])

  useEffect(() => {
    if (!organizationTeams) return;

    const teamIdsWithMembers = new Set<string>();
    (organizationTeams as any[]).forEach((team: any) => {
      if (Array.isArray(team?.members) && team.members.length > 0) {
        teamIdsWithMembers.add(String(team.id));
      }
    });

    (resources || []).filter((res: any) => res?.is_deleted === false).forEach((res: any) => {
      if (Array.isArray(res?.teams)) {
        res.teams.forEach((t: any) => t?.id != null && teamIdsWithMembers.add(String(t.id)));
      }
    });

    const teamOptions: SelectOption[] = (organizationTeams as any[])
      .filter((team: any) => team && team.name && String(team.name).trim())
      .filter((team: any) => team?.is_deactivated === false)
      .filter((team: any) => teamIdsWithMembers.has(String(team.id)))
      .map((team: any) => {
        const projectNames: string[] = Array.isArray(team.projects)
          ? team.projects.map((p: any) => p?.project_name).filter(Boolean)
          : [];
        const projectsSuffix = projectNames.length ? ` (${projectNames.join(', ')})` : '';
        const fullLabel = `${team.name}${projectsSuffix}`;
        return {
          value: String(team.id),
          label: truncateText(fullLabel, 50)
        } as SelectOption;
      })
      .sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label));

    setAvailableTeams(teamOptions);

    if (searchType === 'team') {
      // Don't make any decisions while teams are still loading
      if (teamsLoading) {
        return;
      }

      // If no teams are available after loading, fallback to 'people' filter
      if (teamOptions.length === 0) {
        setFilters({ type: 'people' });
        dispatch(updateCapacityPlanningFilters({ filters: { searchType: 'people' }, orgId: org_id }));
      } else {
        // If we have a persisted team from localStorage, try to restore it
        if (!selectedTeam && teamFilterState.team_id) {
          const persistedTeamOption = teamOptions.find(t => t.value === teamFilterState.team_id);
          if (persistedTeamOption) {
            setFilters({ team: persistedTeamOption.value });
            dispatch(updateCapacityPlanningFilters({ filters: { selectedTeam: persistedTeamOption.value }, orgId: org_id }));
            return; // Exit early, let the team selection useEffect handle the rest
          }
        }

        // If teams are available, ensure selectedTeam is valid
        // If no team selected or selected team is not in available teams, select first team
        if (!selectedTeam || !teamOptions.find(t => t.value === selectedTeam)) {
          setFilters({ team: teamOptions[0].value });
          dispatch(updateCapacityPlanningFilters({ filters: { selectedTeam: teamOptions[0].value }, orgId: org_id }));
        }
      }
    }
  }, [organizationTeams, searchType, selectedTeam, resources, teamFilterState.team_id, teamsLoading, dispatch, org_id, setFilters]);

  // Prepare available sprints from resourcePageSprints
  useEffect(() => {
    if (!resourcePageSprints || !Array.isArray(resourcePageSprints) || resourcePageSprints.length === 0) {
      setAvailableSprints([]);
      return;
    }

    // Separate sprints: valid (with dates) vs invalid (without dates)
    const validSprints = resourcePageSprints.filter((s: any) => s?.name && s?.start_date && s?.end_date);
    const invalidSprints = resourcePageSprints.filter((s: any) => s?.name && (!s?.start_date || !s?.end_date));

    // Sort valid sprints by status priority, then by date
    const sortedSprints = [...validSprints].sort((a: any, b: any) => {
      const priorityDiff = getStatusPriority(a.status) - getStatusPriority(b.status);
      if (priorityDiff !== 0) return priorityDiff;

      // Within same status: past = most recent first, others = chronological
      const aTime = new Date(a.start_date).getTime();
      const bTime = new Date(b.start_date).getTime();
      return getStatusPriority(a.status) === 1 ? bTime - aTime : aTime - bTime;
    });

    // Group valid sprints by composite key (name + normalized status)
    const sprintsByKey = new Map<string, any[]>();
    sortedSprints.forEach((sprint: any) => {
      const key = buildSprintKey(sprint.name, sprint.status);
      const existing = sprintsByKey.get(key) || [];
      existing.push(sprint);
      sprintsByKey.set(key, existing);
    });

    // Build lookup map and active team IDs for team-availability checks
    const activeTeamIds = getActiveTeamIds(organizationTeams as any[] | null, resources as any[] | null);
    const teamByTeamId = organizationTeams
      ? new Map((organizationTeams as any[]).map((t: any) => [String(t.team_id), t]))
      : null;

    // Build options from valid sprints, hiding those where all teams are deactivated
    const sprintOptions: SelectOption[] = Array.from(sprintsByKey.entries())
      .map(([compositeKey, sprints]) => {
        const sprintName = getSprintNameFromKey(compositeKey);
        const representative = sprints[0];
        const statusText = getStatusDisplayText(representative.status);

        // Determine if this sprint should be enabled, disabled, or hidden
        const teamStatus = getSprintTeamStatus(sprints, teamByTeamId, activeTeamIds);

        return {
          value: compositeKey,
          label: `${sprintName} (${statusText})`,
          // Store all sprints with this name+status in metadata
          disabled: teamStatus.action === 'disable',
          disabledReason: teamStatus.reason,
          _hide: teamStatus.action === 'hide', // Internal flag to filter out
          metadata: {
            sprint_name: sprintName,
            sprints, // Array of all sprints with this name and status
            status: representative.status,
            start_date: representative.start_date,
            end_date: representative.end_date
          }
        };
      })
      .filter((opt: any) => !opt._hide) // Remove sprints where all teams are deactivated
      .sort((a, b) => {
        const nameA = getSprintNameFromKey(a.value);
        const nameB = getSprintNameFromKey(b.value);
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      });

    // Add disabled entries for sprints missing dates (deduplicated by name)
    const addedInvalidNames = new Set<string>();
    invalidSprints.forEach((sprint: any) => {
      const alreadyExists = sprintOptions.some((opt: any) => opt.metadata?.sprint_name === sprint.name);
      if (!alreadyExists && !addedInvalidNames.has(sprint.name)) {
        addedInvalidNames.add(sprint.name);

        // Collect all invalid sprints with this name to gather team info
        const matchingSprints = invalidSprints.filter((s: any) => s.name === sprint.name);

        // Reuse the same team status check — if all teams are deactivated, hide entirely
        const teamStatus = getSprintTeamStatus(matchingSprints, teamByTeamId, activeTeamIds);
        if (teamStatus.action === 'hide') return;

        // Determine which dates are missing for a specific message
        const missingStart = !sprint.start_date;
        const missingEnd = !sprint.end_date;
        let dateReason = 'start and end dates';
        if (missingStart && !missingEnd) dateReason = 'a start date';
        else if (!missingStart && missingEnd) dateReason = 'an end date';

        // Combine date message with detailed team breakdown
        const dateMessage = `This sprint does not have ${dateReason} set`;

        // Build team breakdown: show imported teams and not-imported teams separately
        const importedTeams: string[] = [];
        const notImportedTeams: string[] = [];
        matchingSprints.forEach((s: any) => {
          if (!s.team_name) return;
          const label = s.project_name ? `${s.team_name} (${s.project_name})` : s.team_name;
          const orgTeam = teamByTeamId?.get(String(s.team_id));
          if (!orgTeam) {
            notImportedTeams.push(label);
          } else if (orgTeam.is_deactivated !== true) {
            importedTeams.push(label);
          }
        });

        const teamLines: string[] = [];
        if (importedTeams.length > 0) {
          teamLines.push(Array.from(new Set(importedTeams)).join(', '));
        }
        if (notImportedTeams.length > 0) {
          teamLines.push(`${Array.from(new Set(notImportedTeams)).join(', ')} – not yet imported`);
        }

        const teamInfo = teamLines.length > 0
          ? `\nTeams associated to this sprint:\n${teamLines.join('\n')}`
          : '';

        const disabledReason = `${dateMessage}${teamInfo}`;

        sprintOptions.push({
          value: buildSprintKey(sprint.name, sprint.status || 'future'),
          label: sprint.name,
          disabled: true,
          disabledReason,
          metadata: {
            sprint_name: sprint.name,
            sprints: matchingSprints,
            status: sprint.status || 'future',
            start_date: null,
            end_date: null
          }
        } as any);
      }
    });

    setAvailableSprints(sprintOptions);
    setSortedSprintsForFilter(sortedSprints);

    // Auto-select current sprint on initial load if searchType is 'sprint'
    if (searchType === 'sprint' && !selectedSprint && sprintOptions.length > 0) {
      // Only consider enabled (non-disabled) sprints for auto-selection
      const enabledOptions = sprintOptions.filter((opt: any) => !opt.disabled);
      if (enabledOptions.length > 0) {
        const currentSprint = enabledOptions.find((opt: any) => opt.metadata?.status === 'current');
        if (currentSprint) {
          dispatch(updateCapacityPlanningFilters({ filters: { selectedSprint: currentSprint.value }, orgId: org_id }));
        } else {
          // Fallback to the first enabled sprint if no current sprint found
          dispatch(updateCapacityPlanningFilters({ filters: { selectedSprint: enabledOptions[0].value }, orgId: org_id }));
        }
      }
    }
  }, [resourcePageSprints, searchType, selectedSprint, organizationTeams, resources]);

  // Auto-select logic and validation
  useEffect(() => {
    if (searchType !== 'sprint' || !selectedSprint) {
      return;
    }

    // Don't run if data isn't loaded yet - prevents clearing persisted selections
    if (!organizationTeams || !availableSprints.length || teamsLoading) {
      return;
    }

    // Logic to handle selection (Validation + Auto-select)
    if (availableTeamsForSprint.length > 0) {
      // Use selectedTeams directly from Redux (not ref) to correctly detect persisted values on page refresh
      const currentSelected = selectedTeams || [];

      // 1. Validate current selection against available options
      const validSelected = currentSelected.filter((id: string) => availableTeamsForSprint.some(t => t.value === id));

      // 2. Decide what to do
      let newSelection: string[] = [];
      let shouldUpdate = false;

      // Check if we need to auto-select (Sprint change or First load)
      const isSprintChange = lastAutoSelectedSprint.current !== selectedSprint;

      if (isSprintChange) {
        // Sprint changed or first load.
        // If we have valid teams from previous selection (or persistence), keep them.
        if (validSelected.length > 0) {
          // We have valid teams! Keep them.
          // Only update Redux if the selection actually changed (e.g. some teams were dropped)
          if (validSelected.length !== currentSelected.length) {
            newSelection = validSelected;
            shouldUpdate = true;
          }
        } else {
          // No valid teams from previous selection. Auto-select first team.
          newSelection = [String(availableTeamsForSprint[0].value)];
          shouldUpdate = true;
        }
        lastAutoSelectedSprint.current = selectedSprint;
      } else {
        // Sprint did NOT change. Just validate existing selection.
        if (validSelected.length !== currentSelected.length) {
          newSelection = validSelected;
          shouldUpdate = true;
        }
      }

      if (shouldUpdate) {
        dispatch(updateCapacityPlanningFilters({ filters: { selectedTeams: newSelection }, orgId: org_id }));
      }
    }
    // Note: Do NOT clear selectedTeams when availableTeamsForSprint is empty.
    // This could happen during initial load before data is ready, and we don't want to lose persisted values.
  }, [searchType, selectedSprint, availableTeamsForSprint, selectedTeams, organizationTeams, availableSprints, teamsLoading, dispatch, org_id]);

  // Reset ref when leaving sprint mode
  useEffect(() => {
    if (searchType !== 'sprint') {
      lastAutoSelectedSprint.current = null;
    }
  }, [searchType]);

  // When a single team is selected in sprint mode, create a sorted sprint list for that team only
  // Note: Sprint navigation only makes sense when exactly one team is selected
  useEffect(() => {
    // Sprint navigation requires exactly one team selected
    if (searchType !== 'sprint' || !selectedTeams || selectedTeams.length !== 1 || !sortedSprintsForFilter.length) {
      setTeamSprintsForNavigation([]);
      return;
    }

    const singleTeamId = selectedTeams[0];
    // Find the selected team to get its team_id
    const team = organizationTeams?.find((t: any) => String(t.id) === String(singleTeamId));
    if (!team || !team.team_id) {
      setTeamSprintsForNavigation([]);
      return;
    }

    const teamUUID = team.team_id;

    // Filter sprints for this specific team
    const teamSprints = sortedSprintsForFilter.filter((sprint: any) =>
      String(sprint.team_id) === String(teamUUID)
    );

    // Sort by date (chronological order for navigation)
    const sortedTeamSprints = [...teamSprints].sort((a: any, b: any) => {
      // Handle null dates - put them at the end
      if (!a.start_date && !b.start_date) return 0;
      if (!a.start_date) return 1;
      if (!b.start_date) return -1;
      return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
    });

    setTeamSprintsForNavigation(sortedTeamSprints);

    // Update current index based on selected sprint
    const selectedSprintName = getSprintNameFromKey(selectedSprint);
    const currentIndex = sortedTeamSprints.findIndex((s: any) => s.name === selectedSprintName);
    if (currentIndex >= 0) {
      setCurrentSprintIndexForFilter(currentIndex);
    }
  }, [searchType, selectedTeams, selectedSprint, sortedSprintsForFilter, organizationTeams]);

  // Calculate sprints to display based on sprint range selection (bucket approach)
  // Reuses teamSprintsForNavigation (already sorted chronologically) and picks 1-3 sprints max
  useEffect(() => {
    // Only run in sprint filter mode
    if (searchType !== 'sprint' || !selectedSprint) {
      dispatch(setTeamSprints([]));
      return;
    }

    // If multiple teams selected, only show the selected sprint (current behavior)
    if (selectedTeams && selectedTeams.length > 1) {
      // Find sprints matching selected sprint name for all selected teams
      const filteredSprints = resourcePageSprints?.filter((s: any) => {
        if (!s || !s.name || !s.start_date || !s.end_date) return false;
        if (s.name !== getSprintNameFromKey(selectedSprint)) return false;

        const team = organizationTeams?.find((t: any) => String(t.team_id) === String(s.team_id));
        if (!team || !selectedTeams.includes(String(team.id))) return false;

        // Filter by project_id if team has projects
        if (team.projects && team.projects.length > 0) {
          const projectId = team.projects[0]?.project_id;
          if (projectId && String(s.project_id) !== String(projectId)) return false;
        }
        return true;
      }) || [];

      // Sort sprints so 'current' status renders on top (last in array)
      filteredSprints.sort((a: any, b: any) => {
        const getScore = (status: string) => {
          if (status === 'current') return 3;
          if (status === 'future') return 2;
          return 1;
        };
        return getScore(a.status) - getScore(b.status);
      });

      dispatch(setTeamSprints(filteredSprints));
      return;
    }

    // If no teams selected, only show the selected sprint
    if (!selectedTeams || selectedTeams.length === 0) {
      // Find all sprints with the selected name
      const matchingSprints = resourcePageSprints?.filter((s: any) => s.name === getSprintNameFromKey(selectedSprint) && s.start_date && s.end_date) || [];
      
      // Prioritize finding a 'current' sprint to display
      const currentSprint = matchingSprints.find((s: any) => s.status === 'current');
      // Fallback to the first matching sprint if no current one exists
      const sprint = currentSprint || matchingSprints[0];

      dispatch(setTeamSprints(sprint ? [sprint] : []));
      return;
    }

    // For single team: reuse teamSprintsForNavigation (already sorted chronologically)
    if (!teamSprintsForNavigation || teamSprintsForNavigation.length === 0) {
      dispatch(setTeamSprints([]));
      return;
    }

    const sortedSprints = teamSprintsForNavigation;
    const currentSprintIndex = currentSprintIndexForFilter;

    // If current sprint not found, only show the selected sprint if it exists
    if (currentSprintIndex < 0) {
      const sprint = sortedSprints.find((s: any) => s.name === getSprintNameFromKey(selectedSprint));
      dispatch(setTeamSprints(sprint ? [sprint] : []));
      return;
    }

    // Pick sprints based on range selection (exact counts)
    let sprintsToShow: any[] = [];

    if (sprintRangeSelection === 'current') {
      // Only current sprint (1 sprint)
      sprintsToShow = [sortedSprints[currentSprintIndex]];
    } else if (sprintRangeSelection === 'past-current') {
      // 1 past + 1 current = 2 sprints
      if (currentSprintIndex > 0) {
        // We have at least one past sprint
        sprintsToShow = [sortedSprints[currentSprintIndex - 1], sortedSprints[currentSprintIndex]];
      } else {
        // No past sprint available, only show current
        sprintsToShow = [sortedSprints[currentSprintIndex]];
      }
    } else if (sprintRangeSelection === 'current-future') {
      // 1 current + 1 future = 2 sprints
      if (currentSprintIndex < sortedSprints.length - 1) {
        // We have at least one future sprint
        sprintsToShow = [sortedSprints[currentSprintIndex], sortedSprints[currentSprintIndex + 1]];
      } else {
        // No future sprint available, only show current
        sprintsToShow = [sortedSprints[currentSprintIndex]];
      }
    } else if (sprintRangeSelection === 'all') {
      // 1 past + 1 current + 1 future = 3 sprints
      const hasPast = currentSprintIndex > 0;
      const hasFuture = currentSprintIndex < sortedSprints.length - 1;

      if (hasPast && hasFuture) {
        // All three available
        sprintsToShow = [
          sortedSprints[currentSprintIndex - 1],
          sortedSprints[currentSprintIndex],
          sortedSprints[currentSprintIndex + 1]
        ];
      } else if (hasPast && !hasFuture) {
        // No future, show 1 past + current = 2 sprints
        sprintsToShow = [sortedSprints[currentSprintIndex - 1], sortedSprints[currentSprintIndex]];
      } else if (!hasPast && hasFuture) {
        // No past, show current + 1 future = 2 sprints
        sprintsToShow = [sortedSprints[currentSprintIndex], sortedSprints[currentSprintIndex + 1]];
      } else {
        // No past or future, only show current = 1 sprint
        sprintsToShow = [sortedSprints[currentSprintIndex]];
      }
    }

    dispatch(setTeamSprints(sprintsToShow));
  }, [searchType, selectedSprint, sprintRangeSelection, selectedTeams, teamSprintsForNavigation, currentSprintIndexForFilter, resourcePageSprints, organizationTeams, dispatch]);

  // Update date range based on displayed sprints
  useEffect(() => {
    if (searchType !== 'sprint' || !selectedSprint) {
      return;
    }

    if (!teamSprints || teamSprints.length === 0) {
      return;
    }

    // Calculate date range from earliest to latest sprint
    const dates = teamSprints
      .map((s: any) => [s.start_date, s.end_date])
      .flat()
      .filter(Boolean)
      .map((d: string) => moment(d));

    if (dates.length === 0) {
      return;
    }

    const earliestDate = moment.min(dates);
    const latestDate = moment.max(dates);

    const newStartDate = earliestDate.format('YYYY-MM-DD');
    const newEndDate = latestDate.format('YYYY-MM-DD');

    // Update date range in Redux
    if (!currentDateRange ||
      currentDateRange.startDate !== newStartDate ||
      currentDateRange.endDate !== newEndDate) {
      dispatch(setUtilizationCurrentDateRange({ startDate: newStartDate, endDate: newEndDate }));
    }
  }, [searchType, selectedSprint, sprintRangeSelection, selectedTeams, teamSprints, dispatch, currentDateRange]);

  // Update team filter state in Redux when team is selected
  useEffect(() => {
    // If not in team mode or no team selected, reset everything
    if (searchType !== 'team' || !selectedTeam) {
      dispatch(resetTeamFilterState());
      return;
    }

    // If we're in team mode but data isn't loaded yet, wait for it
    if (!organizationTeams || organizationTeams.length === 0) {
      return; // Don't reset, just wait for data to load
    }

    // Find the selected team
    const team = organizationTeams.find((t: any) => String(t.id) === String(selectedTeam));
    if (!team) {
      dispatch(resetTeamFilterState());
      return;
    }

    // Extract team information
    const teamId = String(team.id);
    const teamUUID = team.team_id || '';
    const teamName = team.name || '';
    const teamProject = team.projects?.[0]?.project_id ? String(team.projects[0].project_id) : '';
    const teamProjectName = team.projects?.[0]?.project_name || '';

    // Update team filter state - no sprint mode for teams filter
    dispatch(updateTeamFilterState({
      team_id: teamId,
      team_uuid: teamUUID,
      team_name: teamName,
      team_project: teamProject,
      team_project_name: teamProjectName,
      area_path: '',
      sprint_mode: false
    }));
  }, [searchType, selectedTeam, organizationTeams, dispatch]);

  // Filter resources based on selection (people, skills, teams, and sprints)
  useEffect(() => {
    // Don't filter until groups are available
    if (!groups || groups.length === 0) {
      return;
    }

    // Clear filters when search type changes to non-filter types (project, task)
    if (searchType !== 'people' && searchType !== 'skills' && searchType !== 'team' && searchType !== 'sprint') {
      setFilteredResources(groups);
      const resourceIds = groups.map((r: any) => r.id);
      dispatch(updateCapacityPlanningFilters({ filters: { filteredResourceIds: resourceIds }, orgId: org_id }));
      return;
    }

    let filtered = [...groups];

    // Filter by selected resources (people)
    if (searchType === 'people' && selectedResources.length > 0) {
      filtered = filtered.filter((resource: any) =>
        selectedResources.includes(String(resource.id))
      );
    }

    // Filter by selected skills
    if (searchType === 'skills' && selectedSkills.length > 0) {
      filtered = filtered.filter((resource: any) => {
        if (!resource.skills || !Array.isArray(resource.skills)) return false;
        // Check if resource has at least one of the selected skills
        return selectedSkills.some((skill: string) => resource.skills.includes(skill));
      });
    }

    // Filter by selected sprint
    if (searchType === 'sprint' && selectedSprint) {
      const sprintOption = availableSprints.find((s: any) => s.value === selectedSprint) as any;
      if (sprintOption && sprintOption.metadata) {
        // If teams are selected, filter by those teams only
        if (selectedTeams && selectedTeams.length > 0) {
          filtered = filtered.filter((resource: any) => {
            if (!resource.teams || !Array.isArray(resource.teams)) return false;
            // Check if resource belongs to any of the selected teams
            return resource.teams.some((team: any) =>
              selectedTeams.includes(team.id.toString())
            );
          });
        } else {
          // If no teams selected (All Teams), filter by resources that have data in allocated_task
          // This ensures we only show resources the backend returned data for
          if (capacityPlanningData?.allocated_task) {
            const allocatedResourceIds = Object.keys(capacityPlanningData.allocated_task);
            filtered = filtered.filter((resource: any) =>
              allocatedResourceIds.includes(String(resource.id))
            );
          }
        }
      }
    }

    // Filter by selected team
    if (searchType === 'team' && selectedTeam) {
      filtered = filtered.filter((resource: any) => {
        if (!resource.teams || !Array.isArray(resource.teams)) return false;
        // Check if resource belongs to the selected team
        return resource.teams.some((team: any) => team.id.toString() === selectedTeam);
      });
    } else if (searchType === 'team' && !selectedTeam) {
      // For team filter type with no team selected, show all resources
      filtered = groups;
    }

    // When switching from team to non-team filter, ensure all resources are included
    // This handles the case where searchType changes from 'team' to 'people' or 'skills'
    // and no specific filters are selected
    if (searchType === 'people' && selectedResources.length === 0) {
      filtered = groups;
    }
    if (searchType === 'skills' && selectedSkills.length === 0) {
      filtered = groups;
    }

    setFilteredResources(filtered);
    const resourceIds = filtered.map((r: any) => r.id);
    dispatch(updateCapacityPlanningFilters({ filters: { filteredResourceIds: resourceIds }, orgId: org_id }));
  }, [selectedResources, selectedSkills, selectedTeam, selectedTeams, selectedSprint, availableSprints, groups, searchType, capacityPlanningData, dispatch, org_id]);

  // Separate effect to manage displayResources for TimelineLeftPanel
  // This handles the special case where sprint + specific teams shows all resources from allocated_task
  useEffect(() => {
    // For sprint filter with teams selected, use all resources from allocated_task
    if (searchType === 'sprint' && selectedSprint && selectedTeams && selectedTeams.length > 0 && capacityPlanningData?.allocated_task) {
      const allocatedResourceIds = Object.keys(capacityPlanningData.allocated_task);
      const displayResourcesList: any[] = [];

      allocatedResourceIds.forEach((resourceId: string) => {
        const allocatedData = capacityPlanningData.allocated_task[resourceId];
        const isOptionalResource = allocatedData?.optional_resource === true;

        // Try to find the resource in groups first
        let resource = groups.find((r: any) => String(r.id) === resourceId);

        // If resource not found in groups, create a placeholder resource
        if (!resource) {
          // Try to get resource name from resources array
          const fullResource = resources?.find((r: any) => String(r.id) === resourceId);
          const newResource: any = {
            id: resourceId,
            title: fullResource?.name || `Resource ${resourceId}`,
            rightTitle: fullResource?.skills_name?.join(",") || "",
            skills: fullResource?.skills_name || [],
            teams: fullResource?.teams || [],
            isOptionalResource: isOptionalResource
          };
          displayResourcesList.push(newResource);
        } else {
          // Add optional_resource flag to existing resource
          const updatedResource: any = { ...(resource as any), isOptionalResource: isOptionalResource };
          displayResourcesList.push(updatedResource);
        }
      });

      setDisplayResources(displayResourcesList);
    } else {
      // For all other cases (including sprint with no teams selected), displayResources should match filteredResources
      // But we still need to mark optional resources for visual cues
      const displayResourcesList = filteredResources.map((resource: any) => {
        // Check if this resource is optional in allocated_task
        if (capacityPlanningData?.allocated_task && capacityPlanningData.allocated_task[String(resource.id)]) {
          const allocatedData = capacityPlanningData.allocated_task[String(resource.id)];
          const isOptionalResource = allocatedData?.optional_resource === true;
          return { ...resource, isOptionalResource: isOptionalResource };
        }
        return resource;
      });

      setDisplayResources(displayResourcesList);
    }
  }, [searchType, selectedSprint, selectedTeams, capacityPlanningData, groups, filteredResources, resources]);

  // ============================================================================
  // SPRINT PAYLOAD BUILDING & API CALL
  // ============================================================================

  // Build sprint payload when sprint filter is selected
  useEffect(() => {
    // Exit early if not sprint filter
    if (searchType !== 'sprint') {
      return;
    }

    // Don't build payload if teams, resources, or sprints are still loading
    if (teamsLoading || manageResourcesLoading) {
      return;
    }

    // Prevent API call if in sprint mode and teams are available but none selected
    if (!selectedTeams || selectedTeams.length === 0) {
      if (availableTeamsForSprint.length > 0) {
        return; // Teams available but none selected
      }
    }

    // Check if sprints are still loading
    if (hasActiveIntegrations && !Array.isArray(resourcePageSprints)) {
      return; // Sprints are still loading
    }

    // Wait for teamSprints to be populated if we have teams selected (needed for paths)
    if (selectedSprint && selectedTeams && selectedTeams.length > 0) {
      if (!teamSprints || !Array.isArray(teamSprints)) {
        return; // teamSprints not populated yet, wait for it
      }
    }

    // Only build payload after initialization is complete
    if (!isInitialized || groups.length === 0) {
      return;
    }

    if (!selectedSprint) {
      return;
    }

    // Verify that selectedTeams are valid for the selectedSprint
    if (selectedSprint && selectedTeams && selectedTeams.length > 0 && availableSprints && availableSprints.length > 0 && organizationTeams) {
      const sprintOption = availableSprints.find((s: any) => s.value === selectedSprint) as any;
      if (sprintOption && sprintOption.metadata && sprintOption.metadata.sprints) {
        const validTeamUUIDs = new Set(sprintOption.metadata.sprints.map((s: any) => String(s.team_id)));

        const selectedTeamUUIDs = selectedTeams.map((tId: string) => {
          const team = organizationTeams.find((t: any) => String(t.id) === String(tId));
          return team ? String(team.team_id) : null;
        }).filter(Boolean);

        const allSelectedTeamsValid = selectedTeamUUIDs.length > 0 && selectedTeamUUIDs.every((uuid: string | null) => uuid && validTeamUUIDs.has(uuid));

        if (!allSelectedTeamsValid) {
          return; // Teams not valid for this sprint yet
        }
      }
    }

    // Verify filtered resources match selected teams (if teams selected)
    if (selectedTeams && selectedTeams.length > 0) {
      // CRITICAL: Verify that filtered resources actually belong to the selected teams
      // If they don't match, it means resource filtering hasn't completed yet after team change
      // In this case, skip building payload and wait for resource filtering to complete
      if (groups && groups.length > 0 && capacityPlanningFilters.filteredResourceIds.length > 0) {
        const filteredResources = groups.filter((r: any) =>
          capacityPlanningFilters.filteredResourceIds.includes(r.id)
        );

        // Only check if we have filtered resources to verify
        if (filteredResources.length > 0) {
          // Check if all filtered resources belong to any of the selected teams
          const allResourcesBelongToTeams = filteredResources.every((resource: any) => {
            if (!resource.teams || !Array.isArray(resource.teams)) return false;
            return resource.teams.some((team: any) => selectedTeams.includes(String(team.id)));
          });

          // If resources don't match the selected teams, skip building payload
          // This means teams changed but resource filtering hasn't completed yet
          if (!allResourcesBelongToTeams) {
            return;
          }
        }
        // If filteredResourceIds is empty or no matching resources found, 
        // proceed (could be initial state or filtering in progress - let it build with empty array)
      }
    }

    // Sprint filter is Azure-only
    const sprintType: string | null = azureActive ? 'azure' : null;

    // Get sprint name from selected sprint
    const sprintOption = availableSprints.find((s: any) => s.value === selectedSprint) as any;
    const sprintName = sprintOption?.metadata?.sprint_name;

    // Build sprint API payload - only includes: sprint_names, teams, sprint_type
    const payload: any = {
      sprint_names: null as string[] | null,
      teams: null as Array<{
        team_name: string;
        project_name: string | null;
        paths: string[] | null;
      }> | null,
      sprint_type: sprintType
    };

    // Add sprint_names from teamSprints (the sprints being displayed in timeline)
    // teamSprints contains 1-3 sprints based on sprintRangeSelection
    // This is what's shown on UI with sprint bars
    let sprintNames: string[] = [];
    if (teamSprints && Array.isArray(teamSprints) && teamSprints.length > 0) {
      sprintNames = teamSprints
        .map((s: any) => s.name)
        .filter((name: string) => name != null && name !== '');

      // Remove duplicates from sprint names
      sprintNames = Array.from(new Set(sprintNames));

      // Ensure max 3 sprint names
      sprintNames = sprintNames.slice(0, 3);
    } else if (sprintName) {
      // Fallback: if teamSprints is empty, use the selected sprint name
      sprintNames = [sprintName];
    }

    // Add team info only if teams are selected AND valid for this sprint
    let teamsData: any[] = [];
    if (selectedTeams && selectedTeams.length > 0 && availableTeamsForSprint.length > 0) {
      // Filter to only valid teams for this sprint
      const validTeamIds = selectedTeams.filter((teamId: string) =>
        availableTeamsForSprint.some(t => t.value === teamId)
      );

      if (validTeamIds.length > 0) {
        // Get team data for all selected teams
        teamsData = validTeamIds.map((teamId: string) =>
          organizationTeams?.find((t: any) => String(t.id) === String(teamId))
        ).filter(Boolean);
      }
    }

    // Apply constraints based on backend requirements:
    // 1. If multiple sprint names → only send 1 team object
    // 2. If multiple teams → only send 1 sprint name
    if (sprintNames.length > 1) {
      // Multiple sprints selected - only send 1 team object
      teamsData = teamsData.length > 0 ? [teamsData[0]] : [];
    } else if (teamsData.length > 1) {
      // Multiple teams selected - only send 1 sprint name (sprint range is disabled)
      sprintNames = sprintNames.length > 0 ? [sprintNames[0]] : (sprintName ? [sprintName] : []);
    }

    // Set sprint_names in payload
    payload.sprint_names = sprintNames.length > 0 ? sprintNames : null;

    // Build team objects if we have teams
    if (teamsData.length > 0) {
      payload.teams = teamsData.map((team: any) => {
        // Get team's project name
        const projectName = team.projects?.[0]?.project_name || null;

        // Get paths from teamSprints (the sprints displayed on UI banner)
        // Filter by team_id to get only sprints for this specific team
        let paths: string[] | null = null;
        if (teamSprints && Array.isArray(teamSprints) && teamSprints.length > 0) {
          // Filter teamSprints for this specific team and extract paths
          const teamSprintPaths = teamSprints
            .filter((s: any) => {
              // Match sprint by team_id - only get sprints for this team
              return String(s.team_id) === String(team.team_id);
            })
            .map((s: any) => s.path)
            .filter((path: string | null) => path != null && path !== '');

          paths = teamSprintPaths.length > 0 ? teamSprintPaths : null;
        }

        // Fallback: If no paths from teamSprints, try to get from sprint option metadata
        if (!paths && selectedSprint && availableSprints && availableSprints.length > 0) {
          const sprintOption = availableSprints.find((s: any) => s.value === selectedSprint) as any;
          if (sprintOption && sprintOption.metadata && sprintOption.metadata.sprints) {
            const teamSprint = sprintOption.metadata.sprints.find(
              (s: any) => String(s.team_id) === String(team.team_id)
            );
            if (teamSprint && teamSprint.path) {
              paths = [teamSprint.path];
            }
          }
        }

        return {
          team_name: team.name,
          project_name: projectName,
          paths: paths // Array of paths from sprints banner
        };
      });
    } else {
      // If no teams selected, teams remains null (meaning all teams)
      payload.teams = null;
    }

    // Store sprint payload in Redux
    dispatch(setCapacityPlanningPayload(payload));
  }, [
    searchType,
    selectedSprint,
    selectedTeams,
    sprintRangeSelection,
    teamSprints,
    availableSprints,
    availableTeamsForSprint,
    organizationTeams,
    teamsLoading,
    manageResourcesLoading,
    hasActiveIntegrations,
    resourcePageSprints,
    isInitialized,
    groups.length,
    capacityPlanningFilters.filteredResourceIds,
    dispatch,
  ]);

  // ============================================================================
  // NON-SPRINT PAYLOAD BUILDING
  // ============================================================================

  // Build non-sprint payload when non-sprint filter is selected
  useEffect(() => {
    // Exit early if sprint filter
    if (searchType === 'sprint') {
      return;
    }

    // Don't build payload if teams or resources are still loading
    if (teamsLoading || manageResourcesLoading) {
      return;
    }

    // Only build payload after initialization is complete
    if (!isInitialized || groups.length === 0) {
      return;
    }

    // When in team mode, ensure team filter state is populated
    if (searchType === 'team' && !teamFilterState.team_name) {
      return;
    }

    // Determine resource IDs to use
    let resourceIds: number[];
    if (searchType === 'team') {
      resourceIds = capacityPlanningFilters.filteredResourceIds;
    } else if (searchType === 'people' && selectedResources.length > 0) {
      resourceIds = capacityPlanningFilters.filteredResourceIds;
    } else if (searchType === 'skills' && selectedSkills.length > 0) {
      resourceIds = capacityPlanningFilters.filteredResourceIds;
    } else {
      resourceIds = groups.map((r: any) => r.id);
      // Update Redux to keep it in sync
      if (capacityPlanningFilters.filteredResourceIds.length !== resourceIds.length ||
        !resourceIds.every(id => capacityPlanningFilters.filteredResourceIds.includes(id))) {
        dispatch(updateCapacityPlanningFilters({ filters: { filteredResourceIds: resourceIds }, orgId: org_id }));
      }
    }

    // Determine date range
    let startDate: string;
    let endDate: string;
    if (currentDateRange && currentDateRange.startDate && currentDateRange.endDate) {
      startDate = currentDateRange.startDate;
      endDate = currentDateRange.endDate;
    } else {
      const defaultRange = getDefaultDateRange();
      startDate = defaultRange.startDate;
      endDate = defaultRange.endDate;
      dispatch(setUtilizationCurrentDateRange({ startDate, endDate }));
    }

    // Build non-sprint API payload - NO sprint-related fields
    const payload: any = {
      start_date: startDate,
      end_date: endDate,
      resource_ids: resourceIds,
      filter_type: (searchType === 'team' ? 'team' : 'resource') as 'team' | 'resource',
      teams: null
    };

    // Store non-sprint payload in Redux
    dispatch(setCapacityPlanningPayload(payload));
  }, [
    searchType,
    selectedResources,
    selectedSkills,
    selectedTeam,
    teamFilterState.team_name,
    capacityPlanningFilters.filteredResourceIds,
    currentDateRange?.startDate,
    currentDateRange?.endDate,
    groups.length,
    teamsLoading,
    manageResourcesLoading,
    isInitialized,
    dispatch,
    org_id,
  ]);

  // ============================================================================
  // SPRINT API CALL
  // ============================================================================

  // Call sprint API when sprint payload changes
  useEffect(() => {
    // Exit early if not sprint filter
    if (searchType !== 'sprint') {
      return;
    }

    if (!capacityPlanningPayload || capacityPlanningPayload.sprint_names === undefined) {
      return;
    }

    // Don't fetch if already loading or data still loading
    if (capacityPlanningLoading || teamsLoading || manageResourcesLoading) {
      return;
    }

    // Check if sprints are still loading
    const hasTeamWithPath = capacityPlanningPayload.teams?.some((t: any) => t.paths && Array.isArray(t.paths) && t.paths.length > 0) ?? false;
    if ((hasActiveIntegrations || hasTeamWithPath) && !Array.isArray(resourcePageSprints)) {
      return; // Sprints are still loading
    }

    // Guard: If sprint_names is empty/null, don't call API
    if (!capacityPlanningPayload.sprint_names || capacityPlanningPayload.sprint_names.length === 0) {
      return;
    }

    // Guard: Verify that sprint_names in payload match the currently selected sprint
    // This prevents calling API with old sprint + new teams during sprint change transition
    if (selectedSprint && availableSprints && availableSprints.length > 0) {
      const sprintOption = availableSprints.find((s: any) => s.value === selectedSprint) as any;
      const currentSprintName = sprintOption?.metadata?.sprint_name;

      if (currentSprintName) {
        // Check if any sprint_name in payload matches the current selected sprint
        const payloadContainsCurrentSprint = capacityPlanningPayload.sprint_names.some(
          (name: string) => name === currentSprintName
        );

        if (!payloadContainsCurrentSprint) {
          // Payload contains old sprint names, but selected sprint has changed
          // This means we're in a transition state - wait for payload to update
          return;
        }
      }
    }

    // Create payload key to detect changes (include filters)
    const payloadKey = JSON.stringify({
      sprint_names: capacityPlanningPayload.sprint_names,
      teams: capacityPlanningPayload.teams,
      sprint_type: capacityPlanningPayload.sprint_type,
      filters: sprintFilters || { skills: [], work_item_type: [] },
    });

    // Only call API if payload actually changed
    if (previousPayloadRef.current === payloadKey) {
      return;
    }

    // Update ref and make API call
    previousPayloadRef.current = payloadKey;

    const sprintPayload: any = {
      sprint_names: capacityPlanningPayload.sprint_names,
      teams: capacityPlanningPayload.teams,
      sprint_type: capacityPlanningPayload.sprint_type,
      filters: sprintFilters || { skills: [], work_item_type: [] },
    };
    fetchCapacityPlanningSprint(sprintPayload);
  }, [
    searchType,
    selectedSprint,
    availableSprints,
    capacityPlanningPayload,
    capacityPlanningLoading,
    teamsLoading,
    manageResourcesLoading,
    resourcePageSprints,
    hasActiveIntegrations,
    fetchCapacityPlanningSprint,
    sprintFilters,
  ]);

  // ============================================================================
  // NON-SPRINT API CALL
  // ============================================================================

  // Call non-sprint API when non-sprint payload changes
  useEffect(() => {
    // Exit early if sprint filter
    if (searchType === 'sprint') {
      return;
    }

    if (!capacityPlanningPayload || capacityPlanningPayload.sprint_names !== undefined) {
      return;
    }

    // Don't fetch if already loading or data still loading
    if (capacityPlanningLoading || teamsLoading || manageResourcesLoading) {
      return;
    }

    // Create payload key to detect changes
    const payloadKey = JSON.stringify({
      start_date: capacityPlanningPayload.start_date,
      end_date: capacityPlanningPayload.end_date,
      resource_ids: capacityPlanningPayload.resource_ids,
      filter_type: capacityPlanningPayload.filter_type,
      teams: capacityPlanningPayload.teams,
    });

    // Only call API if payload actually changed
    if (previousPayloadRef.current === payloadKey) {
      return;
    }

    // Update ref and make API call
    previousPayloadRef.current = payloadKey;
    fetchCapacityPlanning(capacityPlanningPayload);
  }, [
    searchType,
    capacityPlanningPayload,
    capacityPlanningLoading,
    teamsLoading,
    manageResourcesLoading,
    fetchCapacityPlanning,
  ]);

  // Filter tasks based on search text and search type
  useEffect(() => {
    const shouldFilterByText = searchType === 'task' || searchType === 'project';
    const extractedTasks = extractCapacityPlanningTasks(capacityPlanningData);

    // For non-text filters, always show full task data
    if (!shouldFilterByText) {
      setFilteredTasks(extractedTasks);
      return;
    }

    // For text-based filters, if no text or no data, show full task data
    if (!debouncedSearchText || Object.keys(extractedTasks).length === 0) {
      setFilteredTasks(extractedTasks);
      return;
    }

    const searchTermLower = debouncedSearchText.toLowerCase();
    const filtered: Record<string, any> = {};

    // Loop through each resource and filter their tasks
    Object.keys(extractedTasks).forEach(resourceId => {
      const resourceTasks = extractedTasks[resourceId];
      const filteredResourceTasks: Record<string, any> = {};

      // Loop through each date and filter the tasks
      Object.keys(resourceTasks).forEach(date => {
        const tasksForDate = resourceTasks[date];
        const filteredTasks = tasksForDate.filter((task: any) => {
          switch (searchType) {
            case 'project':
              return task.project_name?.toLowerCase().includes(searchTermLower);
            case 'task':
            default:
              return task.task_title?.toLowerCase().includes(searchTermLower);
          }
        });

        if (filteredTasks.length > 0) {
          filteredResourceTasks[date] = filteredTasks;
        }
      });

      if (Object.keys(filteredResourceTasks).length > 0) {
        filtered[resourceId] = filteredResourceTasks;
      }
    });

    setFilteredTasks(filtered);
  }, [debouncedSearchText, capacityPlanningData, searchType]);

  const handleTaskSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    dispatch(updateCapacityPlanningFilters({ filters: { searchText: val }, orgId: org_id }));
  }

  // Handle date range changes from Timeline component (navigations)
  const handleDateRangeChange = (startDate: string, endDate: string, resourceIds: number[]) => {
    // Update date range in URL
    // Sprint filter handles its own date range via selected sprint
    if (searchType !== 'sprint') {
      setFilters({ startDate, endDate });
    }
  };

  const handleResourceFilterChange = (selectedOptions: SelectOption[]) => {
    hierarchicalFilterRef.current?.close();
    setSelectedResourceOptions(selectedOptions);
    const selectedIds = selectedOptions.map((option) => String(option.value));
    setFilter('resources', selectedIds);
  }

  const handleSkillFilterChange = (selectedOptions: SelectOption[]) => {
    hierarchicalFilterRef.current?.close();
    setSelectedSkillOptions(selectedOptions);
    const selectedSkillNames = selectedOptions.map((option) => option.value);
    setFilter('skills', selectedSkillNames);
  }

  const handleTeamFilterChange = (option: any) => {
    const value = (option as string | null) || '';
    setFilter('team', value);
  }

  const handleSearchTypeChange = (value: string | null) => {
    hierarchicalFilterRef.current?.close();
    // Default to 'people'
    const newSearchType = (value || 'people') as SearchType;

    // Prevent switching to sprint filter if Azure is not active
    if (newSearchType === 'sprint' && !azureActive) {
      // Fallback to 'people' filter if trying to use sprint without Azure
      const fallbackType = 'people' as SearchType;
      dispatch(updateCapacityPlanningFilters({ filters: { searchType: fallbackType }, orgId: org_id }));
      return;
    }

    // Wrap the actual filter change logic
    const performFilterChange = () => {
      // Build update object to batch all changes in a single dispatch
      const updates: any = { searchType: newSearchType };

      // If user switches to sprint filter, preserve persisted values or auto-select
      if (newSearchType === 'sprint') {
        if (availableSprints && availableSprints.length > 0) {
          // First, check if there's a persisted selectedSprint that's still valid and not disabled
          const persistedOption = selectedSprint && availableSprints.find((opt: any) => opt.value === selectedSprint && !opt.disabled);
          if (persistedOption) {
            // Persisted sprint is still valid, preserve it (don't overwrite)
            // Don't add to updates - let the persisted value remain
          } else {
            // No valid persisted sprint, auto-select current sprint or first enabled one
            const enabledOptions = availableSprints.filter((opt: any) => !opt.disabled);
            if (enabledOptions.length > 0) {
              const currentSprint = enabledOptions.find((opt: any) => opt.metadata?.status === 'current');
              if (currentSprint) {
                updates.selectedSprint = currentSprint.value;
              } else {
                updates.selectedSprint = enabledOptions[0].value;
              }
            }
          }
          // Note: selectedTeams is preserved automatically since we're not clearing it
          // The auto-select logic in useEffect will handle team selection if needed
        }
        // Preserve sprint range selection from localStorage (don't reset to 'current')
        // sprintRangeSelection is already persisted in localStorage and will be loaded automatically
        // Only set default if it's not already set
        if (!sprintRangeSelection || sprintRangeSelection === '') {
          updates.sprintRangeSelection = 'current';
        }
        // Otherwise, preserve the persisted value by not adding it to updates
      } else {
        // When switching to non-sprint filters, reset date range to ±2 weeks from today
        const defaultRange = getDefaultDateRange();
        dispatch(setUtilizationCurrentDateRange({
          startDate: defaultRange.startDate,
          endDate: defaultRange.endDate
        }));
        // Clear team sprints when leaving sprint mode
        dispatch(setTeamSprints([]));
      }

      // If user switches to team filter, try to restore last selected team or use first available
      if (newSearchType === 'team') {
        if (availableTeams && availableTeams.length > 0) {
          // First, try to restore persisted team from teamFilterState
          if (teamFilterState.team_id) {
            const persistedTeamOption = availableTeams.find(t => t.value === teamFilterState.team_id);
            if (persistedTeamOption) {
              updates.selectedTeam = persistedTeamOption.value;
            } else {
              // Persisted team not found in available teams, use first team
              updates.selectedTeam = availableTeams[0].value;
            }
          } else if (selectedTeam && availableTeams.find(t => t.value === selectedTeam)) {
            // Keep current selectedTeam if it's still valid
            updates.selectedTeam = selectedTeam;
          } else {
            // No persisted team and no valid current selection, use first team
            updates.selectedTeam = availableTeams[0].value;
          }
        } else {
          updates.selectedTeam = '';
        }
      }
      // Note: updateCapacityPlanningFilters will automatically clear filters that don't apply to the new type

      // Update URL filters sequentially (the hook uses functional state updates for searchParams so it's safe)
      const urlUpdates: Record<string, string> = {};
      urlUpdates.type = updates.searchType || '';
      
      // If switching to a new type, clear out or set the specific necessary keys
      // The Redux reducer used to do some of this, but now the URL is the source of truth
      if (updates.selectedSprint !== undefined) {
        urlUpdates.sprint = updates.selectedSprint;
      }
      if (updates.selectedTeam !== undefined) {
        urlUpdates.team = updates.selectedTeam;
      }
      
      setFilters(urlUpdates);
    };

    // Check if warning should be shown (only if currently in sprint mode and leaving it)
    if (searchType === 'sprint' && newSearchType !== 'sprint') {
      handleActionWithWarning(performFilterChange);
    } else {
      // No warning needed, proceed directly
      performFilterChange();
    }
  }

  // Listen for external refresh events to re-fetch capacity planning using current UI state
  useEffect(() => {
    const handleExternalRefresh = (_e: Event) => {
      // Respect initialization and existing loading/error states
      if (!isInitialized) return;
      if (capacityPlanningLoading || capacityPlanningError) return;
      if (!capacityPlanningPayload) return;

      // Force API call by clearing the previous payload ref
      // This allows the API useEffect to see the payload as "changed" and trigger a fetch
      previousPayloadRef.current = null;

      // Update payload with same values but new object reference to trigger useEffect
      dispatch(setCapacityPlanningPayload({ ...capacityPlanningPayload }));
    };

    window.addEventListener('capacity-planning:refresh', handleExternalRefresh as EventListener);
    return () => {
      window.removeEventListener('capacity-planning:refresh', handleExternalRefresh as EventListener);
    };
  }, [isInitialized, capacityPlanningLoading, capacityPlanningError, capacityPlanningPayload, dispatch]);

  const getSearchTypePlaceholder = () => {
    switch (searchType) {
      case 'project': return "Search by work unit";
      case 'task':
      default: return "Search by task name";
    }
  }

  const searchTypeOptions: SelectOption[] = React.useMemo(() => {
    const baseOptions: SelectOption[] = [
      { value: 'people', label: 'Filter by People' },
      { value: 'skills', label: 'Filter by Skills' },
      { value: 'project', label: 'Filter by Work Unit' },
      { value: 'task', label: 'Filter by Work Item' },
    ];

    // Add sprint filter only if Azure is active and sprints are available
    if (azureActive && availableSprints && availableSprints.length > 0) {
      baseOptions.push({ value: 'sprint', label: 'Filter by Sprint' });
    }

    // Add team filter if teams are available (regardless of integration)
    if (availableTeams && availableTeams.length > 0) {
      baseOptions.push({ value: 'team', label: 'Filter by Team' });
    }

    return baseOptions;
  }, [availableTeams, availableSprints, azureActive]);

  // Handle sprint filter change
  const handleSprintFilterChange = (option: any) => {
    const value = (option as string | null) || '';
    const performSprintChange = () => {
      setFilter('sprint', value);
      
      // We also need to reset the sprint filters (skills/work_item_type) in Redux directly
      // since they aren't tracked in the URL yet, or we can just let Redux handle it.
      // Actually, since sprintFilters is in Redux, let's keep the dispatch for that specific part
      // to maintain exactly the same behavior.
      dispatch(updateCapacityPlanningFilters({
        filters: {
          sprintRangeSelection: 'current',
          sprintFilters: { skills: [], work_item_type: [] }
        },
        orgId: org_id
      }));
    };
    handleActionWithWarning(performSprintChange);
  };

  // Handle team filter change when in sprint mode (multiselect)
  const handleSprintTeamFilterChange = (selectedOptions: SelectOption[]) => {
    const performTeamChange = () => {
      setSelectedSprintTeamOptions(selectedOptions || []);
      // Extract team IDs from selected options (can be empty array)
      const teamIds = (selectedOptions || []).map(opt => String(opt.value));
      // When more than 1 team is selected, use 'current' sprint only
      const newSprintRangeSelection = teamIds.length > 1 ? 'current' : sprintRangeSelection;
      // Store selected teams for sprint filtering (multiselect) - allows empty array
      // Reset filters when team changes
      setFilter('teams', teamIds);
      
      dispatch(updateCapacityPlanningFilters({
        filters: {
          sprintRangeSelection: newSprintRangeSelection,
          sprintFilters: { skills: [], work_item_type: [] } // Reset filters when team changes
        },
        orgId: org_id
      }));
    };
    handleActionWithWarning(performTeamChange);
  };

  // Filter and sort sprint options
  const filteredSprintOptions = useMemo(() => {
    let filtered = availableSprints;

    // Apply status filter
    if (sprintStatusFilter !== 'all') {
      filtered = availableSprints.filter(opt => {
        const status = (opt as any).metadata?.status;
        if (sprintStatusFilter === 'current') return status === 'current';
        if (sprintStatusFilter === 'past') return status === 'past' || status === 'completed';
        if (sprintStatusFilter === 'future') return status === 'future';
        return true;
      });
    }

    // Apply sorting using moment for date comparisons
    const sorted = [...filtered].sort((a, b) => {
      const aMeta = (a as any).metadata;
      const bMeta = (b as any).metadata;

      if (sprintSortBy === 'name_asc') {
        return getSprintNameFromKey(a.value || '').localeCompare(getSprintNameFromKey(b.value || ''), undefined, { numeric: true, sensitivity: 'base' });
      } else if (sprintSortBy === 'name_desc') {
        return getSprintNameFromKey(b.value || '').localeCompare(getSprintNameFromKey(a.value || ''), undefined, { numeric: true, sensitivity: 'base' });
      } else if (sprintSortBy === 'latest') {
        // Latest sprint = newest/most recent first (descending by start date)
        const aDate = aMeta?.start_date ? moment(aMeta.start_date) : moment(0);
        const bDate = bMeta?.start_date ? moment(bMeta.start_date) : moment(0);
        return bDate.diff(aDate); // Reverse order for latest first
      } else if (sprintSortBy === 'oldest') {
        // Oldest sprint = earliest first (ascending by start date)
        const aDate = aMeta?.start_date ? moment(aMeta.start_date) : moment(0);
        const bDate = bMeta?.start_date ? moment(bMeta.start_date) : moment(0);
        return aDate.diff(bDate);
      }

      return 0;
    });

    return sorted;
  }, [availableSprints, sprintStatusFilter, sprintSortBy]);

  const sprintFilterHeader = (
    <div className="flex p-2 gap-2 justify-between bg-gray-50 dark:bg-gray-800">
      {['all', 'past', 'current', 'future'].map((filter) => (
        <button
          key={filter}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setFilter('sprintStatus', filter);
          }}
          className={`px-2 py-1 text-xs font-medium rounded capitalize transition-colors
            ${sprintStatusFilter === filter
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
        >
          {filter}
        </button>
      ))}
    </div>
  );

  // Render current search component based on search type
  const renderSearchComponent = () => {
    switch (searchType) {
      case 'people':
        return (
          <FilterSelect
            key={`people-select-${searchType}`}
            options={getResourceOptions()}
            placeholder="Select people to filter..."
            value={selectedResourceOptions}
            onChange={handleResourceFilterChange}
            minWidth="min-w-[300px]"
            padding="py-2.5"
          />
        );
      case 'skills':
        return (
          <FilterSelect
            key={`skills-select-${searchType}`}
            options={availableSkills}
            placeholder="Select skills to filter..."
            value={selectedSkillOptions}
            onChange={handleSkillFilterChange}
            minWidth="min-w-[300px]"
            padding="py-2.5"
          />
        );
      case 'sprint':
        return (
          <>
            <CompactSelect
              key={`sprint-select-${searchType}`}
              options={filteredSprintOptions}
              placeholder="Select sprint"
              value={selectedSprint}
              width="min-w-[250px]"
              searchable
              searchPlaceholder="Search sprint..."
              onChange={handleSprintFilterChange}
              customHeader={sprintFilterHeader}
              lookupOptions={availableSprints}
              searchSlot={
                <SprintSortButton
                  sortBy={sprintSortBy || 'latest'}
                  onSortChange={(sortBy) => {
                    hierarchicalFilterRef.current?.close();
                    setFilter('sprintSortBy', sortBy);
                  }}
                />
              }
            />
            {selectedSprint && availableTeamsForSprint && availableTeamsForSprint.length > 0 && (
              <FilterSelect
                key={`sprint-team-select-${searchType}`}
                options={availableTeamsForSprint}
                placeholder="Filter by teams..."
                value={selectedSprintTeamOptions}
                onChange={handleSprintTeamFilterChange}
                minWidth="min-w-[300px]"
                padding="py-2.5"
              />
            )}
            {selectedSprint && (
              <HierarchicalFilterDropdown
                ref={hierarchicalFilterRef}
                skills={availableSkillsForSprintFilter}
                selectedSkills={sprintFilters?.skills || []}
                selectedWorkItemTypes={sprintFilters?.work_item_type || []}
                onSkillsChange={handleSprintFilterSkillsChange}
                onWorkItemTypesChange={handleSprintFilterWorkItemTypesChange}
                onApplyFilters={handleApplySprintFilters}
              />
            )}
            {!teamsLoading && !manageResourcesLoading && isInitialized && availableSprints.length > 0 && selectedSprint && availableTeamsForSprint && availableTeamsForSprint.length === 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-xs whitespace-nowrap">
                <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-yellow-800 dark:text-yellow-300 font-medium">
                  Sprint teams not imported
                </span>
              </div>
            )}
          </>
        );
      case 'team':
        return (
          <CompactSelect
            key={`teams-select-${searchType}`}
            options={availableTeams}
            placeholder="Select team"
            value={selectedTeam}
            width="min-w-[300px]"
            searchable
            searchPlaceholder="Search team..."
            onChange={handleTeamFilterChange}
          />
        );
      case 'project':
      case 'task':
      default:
        return (
          <SearchComponent
            value={localSearchText}
            onChange={handleTaskSearch}
            placeholder={getSearchTypePlaceholder()}
          />
        );
    }
  };

  // Extract tasks from new structure for timeline rendering
  const timelineTasks = React.useMemo(() =>
    extractCapacityPlanningTasks(capacityPlanningData),
    [capacityPlanningData]
  );

  // Determine groups to display
  const groupsToDisplay = React.useMemo(() => {
    // If in sprint mode and sprint is selected
    if (searchType === 'sprint' && selectedSprint) {
      // If no teams are available for this sprint (not imported), show empty array
      if (availableTeamsForSprint.length === 0) {
        return [];
      }
      // If teams are available but none selected, show empty array
      if (!selectedTeams || selectedTeams.length === 0) {
        return [];
      }
    }
    // Otherwise use normal logic
    return displayResources.length > 0 ? displayResources : filteredResources;
  }, [searchType, selectedSprint, selectedTeams, displayResources, filteredResources, availableTeamsForSprint]);

  // Extract skills from displayed resources and map to skill IDs
  const availableSkillsForSprintFilter = useMemo(() => {
    if (searchType !== 'sprint' || !groupsToDisplay || groupsToDisplay.length === 0 || !resources || !Array.isArray(resources)) {
      return [];
    }

    // Collect all unique skill IDs from displayed resources
    const skillIdsSet = new Set<string | number>();

    // Match groupsToDisplay with resources by ID
    groupsToDisplay.forEach((groupResource: any) => {
      const resourceId = groupResource.id;
      // Find matching resource from manage_resources response
      const matchedResource = resources.find((res: any) => String(res.id) === String(resourceId));

      if (matchedResource && matchedResource.skills && Array.isArray(matchedResource.skills)) {
        // skills is an array of skill IDs
        matchedResource.skills.forEach((skillId: string | number) => {
          if (skillId != null) {
            skillIdsSet.add(skillId);
          }
        });
      }
    });


    // Map skill IDs to skill names using organization skills
    const skillOptions: SelectOption[] = [];
    if (organizationSkills && Array.isArray(organizationSkills)) {
      organizationSkills.forEach((skill: any) => {
        // Check if this skill ID is in our set
        if (skillIdsSet.has(String(skill.id))) {
          skillOptions.push({
            value: skill.id,
            label: skill.skill
          });
        }
      });
    }

    // Sort by label
    return skillOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [searchType, groupsToDisplay, resources, organizationSkills]);

  // Handlers for filter changes
  const handleSprintFilterSkillsChange = useCallback((skills: (string | number)[]) => {
    const performFilterChange = () => {
      dispatch(updateCapacityPlanningFilters({
        filters: {
          sprintFilters: {
            ...(sprintFilters || { skills: [], work_item_type: [] }),
            skills
          }
        },
        orgId: org_id
      }));
    };
    handleActionWithWarning(performFilterChange);
  }, [sprintFilters, dispatch, org_id, handleActionWithWarning]);

  const handleSprintFilterWorkItemTypesChange = useCallback((workItemTypes: string[]) => {
    const performFilterChange = () => {
      dispatch(updateCapacityPlanningFilters({
        filters: {
          sprintFilters: {
            ...(sprintFilters || { skills: [], work_item_type: [] }),
            work_item_type: workItemTypes
          }
        },
        orgId: org_id
      }));
    };
    handleActionWithWarning(performFilterChange);
  }, [sprintFilters, dispatch, org_id, handleActionWithWarning]);

  // Combined handler for Apply Filters button (with warning modal)
  const handleApplySprintFilters = useCallback((skills: (string | number)[], workItemTypes: string[]) => {
    const performFilterChange = () => {
      setFilters({
        sprintSkills: skills.map(String),
        sprintWorkItems: workItemTypes
      });
    };
    handleActionWithWarning(performFilterChange);
  }, [setFilters, handleActionWithWarning]);

  // Determine if any filter is active beyond defaults
  const hasActiveFilters = useMemo(() => {
    if (searchType !== 'people') return true;
    if (selectedResources && selectedResources.length > 0) return true;
    if (selectedSkills && selectedSkills.length > 0) return true;
    if (selectedTeam) return true;
    if (selectedTeams && selectedTeams.length > 0) return true;
    if (selectedSprint) return true;
    if (localSearchText) return true;
    if (urlFilters.startDate) return true;
    if (urlFilters.endDate) return true;
    if (Array.isArray(urlFilters.status) && urlFilters.status.length > 0) return true;
    if (Array.isArray(urlFilters.sprintSkills) && (urlFilters.sprintSkills as string[]).length > 0) return true;
    if (Array.isArray(urlFilters.sprintWorkItems) && (urlFilters.sprintWorkItems as string[]).length > 0) return true;
    return false;
  }, [searchType, selectedResources, selectedSkills, selectedTeam, selectedTeams, selectedSprint, localSearchText, urlFilters]);

  const handleClearFilters = () => {
    // Reset all URL filters to defaults
    setFilters({
      type: 'people',
      resources: [],
      skills: [],
      team: '',
      teams: [],
      sprint: '',
      sprintSortBy: 'latest',
      sprintRange: 'current',
      sprintStatus: 'all',
      sprintSkills: [],
      sprintWorkItems: [],
      status: [],
      startDate: '',
      endDate: '',
    });
    // Also reset Redux sprint filters
    dispatch(updateCapacityPlanningFilters({
      filters: {
        searchText: '',
        sprintFilters: { skills: [], work_item_type: [] }
      },
      orgId: org_id
    }));
  };

  return (
    <div className="overflow-hidden dark:bg-[#111928] flex-1 ">
      <TimelineComponent
        tasks={(searchType === 'project' || searchType === 'task')
          ? (debouncedSearchText ? filteredTasks : timelineTasks)
          : timelineTasks}
        suggestedTasks={suggestedTasks?.resources || {}}
        scale={1}
        duration={20}
        groups={groupsToDisplay}
        searchText={debouncedSearchText}
        onDateRangeChange={handleDateRangeChange}
        sprintMode={searchType === 'sprint'}
        currentSprint={searchType === 'sprint' && selectedSprint
          ? (() => {
            const sprintOption = availableSprints.find((s: any) => s.value === selectedSprint) as any;
            if (sprintOption && sprintOption.metadata && sprintOption.metadata.sprints) {
              // If teams are selected, use the first team to find the sprint
              if (selectedTeams && selectedTeams.length > 0) {
                const firstTeamId = selectedTeams[0];
                const team = organizationTeams?.find((t: any) => String(t.id) === String(firstTeamId));
                if (team) {
                  const teamSprint = sprintOption.metadata.sprints.find(
                    (s: any) => String(s.team_id) === String(team.team_id)
                  );
                  if (teamSprint) return teamSprint;
                }
              }
              // Otherwise return the first sprint with this name
              return sprintOption.metadata.sprints[0];
            }
            return null;
          })()
          : null}
        sortedSprints={searchType === 'sprint' ? (selectedTeams && selectedTeams.length === 1 && teamSprintsForNavigation.length > 0 ? teamSprintsForNavigation : sortedSprintsForFilter) : []}
        currentSprintIndex={searchType === 'sprint' ? currentSprintIndexForFilter : -1}
        searchType={searchType}
        availableTeamsForSprint={availableTeamsForSprint}
        unallocatedTasks={capacityPlanningData?.unallocated_task || []}
        statusFilters={Array.isArray(urlFilters.status) ? urlFilters.status as string[] : []}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onSprintNavigate={searchType === 'sprint' ? (index: number) => {
          // Wrap sprint navigation with warning check
          // This covers: Previous Sprint, Next Sprint, and Current Sprint button clicks
          const performSprintNavigation = () => {
            // Handle sprint navigation for sprint filter mode (only works with single team selected)
            const sprintList = selectedTeams && selectedTeams.length === 1 && teamSprintsForNavigation.length > 0
              ? teamSprintsForNavigation
              : sortedSprintsForFilter;

            if (index >= 0 && index < sprintList.length) {
              const newSprint = sprintList[index];
              // Use composite key (name::status) as the value for uniqueness
              dispatch(updateCapacityPlanningFilters({ filters: { selectedSprint: buildSprintKey(newSprint.name, newSprint.status) }, orgId: org_id }));
              setCurrentSprintIndexForFilter(index);
            }
          };
          handleActionWithWarning(performSprintNavigation);
        } : undefined}
        searchBarComponent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search type dropdown */}
            <CompactSelect
              options={searchTypeOptions}
              placeholder="Select search type..."
              value={searchType}
              onChange={handleSearchTypeChange}
            />

            {/* Dynamic search component based on type */}
            {renderSearchComponent()}
          </div>
        }
      />
      {/* Unallocated Tasks Warning Modal */}
      <UnallocatedTasksWarningModal
        showModal={showWarningModal}
        unallocatedTasksCount={unallocatedTasksCount}
        totalStoryPoints={totalStoryPoints}
        setShowModal={setShowWarningModal}
        onConfirm={executePendingAction}
        onCancel={() => {
          setPendingAction(null);
          setShowWarningModal(false);
        }}
      />
    </div>
  )
}


export default ResourcesUtilizations;